// @file Composable for determining whiteboard attachment interactions in play mode
import { trackEvent } from '@@/bits/analytics'
import appCan from '@@/bits/app_can'
import { getStylizedProviderName, urlForOpening as getUrlForOpening } from '@@/bits/attachments'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { isTweet, type PostInteraction } from '@@/bits/post_attachment'
import type { LinkType } from '@@/native_bridge/types'
import { useExpandedPostStore } from '@@/pinia/expanded_post'
import { useNativeAppStore } from '@@/pinia/native_app'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { Post } from '@@/types'
import { handleLinkClick } from '@@/vuecomposables/process_post_or_comment_body'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import { LinkHelpers, type BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import type { PadletAttachmentShape } from '@padlet/whiteboard-schema'
import { storeToRefs } from 'pinia'
import { type TLShape } from 'tldraw'

export const useWhiteboardAttachmentInteraction = (): {
  getWhiteboardAttachmentInteraction: (beethovenDisplayAttributes: BeethovenDisplayAttributes) => PostInteraction
  shouldInteractWithShapeInPlayMode: (shape: TLShape | undefined) => boolean
  shouldShowInteractOutline: (shape: TLShape | undefined) => boolean
  executePlayModeActionForShape: ({ shape, event }: { shape: TLShape | undefined; event?: MouseEvent }) => void
  isMediaContent: (displayAttributes: BeethovenDisplayAttributes) => boolean
} => {
  const { editor: tldrawEditor } = useTldrawEditor()

  const whiteboardPermissionsStore = useWhiteboardPermissionsStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceAttachmentsStore = useSurfaceAttachmentsStore()
  const nativeAppStore = useNativeAppStore()
  const expandedPostStore = useExpandedPostStore()
  const windowSizeStore = useWindowSizeStore()
  const { isSmallerThanDesktop } = storeToRefs(windowSizeStore)
  const surfaceTranslationsStore = useSurfaceTranslationsStore()
  const { isViewingTranslatedPadlet } = storeToRefs(surfaceTranslationsStore)

  const isMediaContent = (displayAttributes: BeethovenDisplayAttributes): boolean => {
    return (
      LinkHelpers.isPhoto(displayAttributes) ||
      LinkHelpers.isVideo(displayAttributes) ||
      LinkHelpers.isAudio(displayAttributes)
    )
  }

  const getWhiteboardAttachmentInteraction = (
    beethovenDisplayAttributes: BeethovenDisplayAttributes,
  ): PostInteraction => {
    const {
      provider_name: providerName,
      is_downloadable: isDownloadable,
      content_category: contentCategory,
      content_subcategory: contentSubcategory,
      embed_code: attachmentEmbedCode,
    } = beethovenDisplayAttributes
    const lowercaseProviderName = getStylizedProviderName(providerName)
    const canDownload = isDownloadable && !device.mobile

    if (isTweet(beethovenDisplayAttributes.metadata?.attachment_props)) {
      // Ensure we never show the Tweet embed when using the new Tweet preview,
      // instead, we take the users to the tweet on Twitter.
      return 'link'
    }

    if (contentCategory === 'video' || contentCategory === 'audio') {
      return 'play'
    }

    if (lowercaseProviderName === 'tiktok') {
      return 'expand'
    }
    if (contentCategory === 'file') {
      return canDownload ? 'download' : 'link'
    }

    if (contentCategory === 'photo') {
      return 'expand'
    }
    if (contentCategory === 'page') {
      return 'link'
    }
    if (attachmentEmbedCode && contentSubcategory !== 'padlet') {
      return 'expand'
    }

    return 'link'
  }

  const shouldInteractWithShapeInPlayMode = (shape: TLShape | undefined): boolean => {
    if (!shape) return false

    const postId = (shape as PadletAttachmentShape)?.props?.postId
    if (!postId) return false

    const post = surfacePostsStore.postEntitiesById[postId]
    if (!post?.cid) return false

    const beethovenData = surfaceAttachmentsStore.getLinkDisplayAttributesForPost(post.cid)
    if (!beethovenData) return false

    const attachmentInteraction = getWhiteboardAttachmentInteraction(beethovenData)

    if (attachmentInteraction === 'play') {
      return true
    }

    if (isMediaContent(beethovenData)) {
      return false
    }

    return ['link', 'expand'].includes(attachmentInteraction)
  }

  const shouldShowInteractOutline = (shape: TLShape | undefined): boolean => {
    if (!shape) return false

    const postId = (shape as PadletAttachmentShape)?.props?.postId
    if (!postId) return false

    const post = surfacePostsStore.postEntitiesById[postId]
    if (!post?.cid) return false

    const beethovenData = surfaceAttachmentsStore.getLinkDisplayAttributesForPost(post.cid)
    if (!beethovenData) return false

    if (!tldrawEditor) return false

    const currentTool = tldrawEditor.getCurrentToolId()
    const isWriterUsingSelectToolWhoCantEditShape =
      isAppUsing('removePlayMode') &&
      currentTool === 'select' &&
      whiteboardPermissionsStore.canIWriteFrame(tldrawEditor.getCurrentPageId()) &&
      !whiteboardPermissionsStore.canIEditShape(shape)

    if (isWriterUsingSelectToolWhoCantEditShape || currentTool === 'interact') {
      const attachmentInteraction = getWhiteboardAttachmentInteraction(beethovenData)

      if (attachmentInteraction === 'play') {
        return false
      }

      if (isMediaContent(beethovenData)) {
        return false
      }

      return ['link', 'expand'].includes(attachmentInteraction)
    }

    return false
  }

  /**
   * Handles attachment interactions in whiteboard play mode
   * Event can be triggered by:
   * - MouseEvent: clicking on the corner arrow in draw mode
   * - undefined: unknown
   *
   * Actions performed based on attachment type:
   * - Native app: delegates to native post handling
   * - Expand: opens post in expanded view
   * - Link: opens external URLs (with safety modal if enabled)
   */
  const executePlayModeActionForShape = ({
    shape,
    event,
  }: {
    shape: TLShape | undefined
    event?: MouseEvent
  }): void => {
    if (!shape) return

    const postId = (shape as PadletAttachmentShape).props.postId
    const post = surfacePostsStore.postEntitiesById[postId]

    const beethovenData = surfaceAttachmentsStore.getLinkDisplayAttributesForPost(post.cid)
    if (!beethovenData) return

    const attachmentInteraction = getWhiteboardAttachmentInteraction(beethovenData)

    if (device.app && appCan('postMessage') && !appCan('nativeExpandedPostV1')) {
      playInNativeApp({ beethovenData, post })
      return
    }

    if (attachmentInteraction === 'expand') {
      if (event instanceof MouseEvent) {
        event.preventDefault()
      }
      expandedPostStore.expandPost({ postCid: post.cid, xTextPanel: !isSmallerThanDesktop.value })
      trackEvent('Surface', 'Expanded post', null, null, { wishId: post.id })
      return
    }

    if (attachmentInteraction === 'link') {
      const url = urlForOpening(shape)

      if (url) {
        if (event instanceof MouseEvent) {
          event.preventDefault()
        }
        handleLinkClick(url, post.author_hashid)
        return
      }
    }

    if (device.app && appCan('postMessage')) {
      // Opening link priority moved to after expand when in app, so use postMessage to open link
      playInNativeApp({ beethovenData, post })
    }
  }

  const urlForDisplay = (post: Post, beethovenData: BeethovenDisplayAttributes): string | null => {
    if (isTweet(post.wish_content?.attachment_props)) {
      return beethovenData.url
    }
    if (beethovenData.url) return beethovenData.url

    return null
  }

  const playInNativeApp = ({
    beethovenData,
    post,
  }: {
    beethovenData: BeethovenDisplayAttributes
    post: Post
  }): void => {
    const {
      content_category: contentCategory,
      content_subcategory: contentSubcategory,
      provider_name: providerName,
    } = beethovenData
    const lowercaseProviderName = getStylizedProviderName(providerName)

    let linkType: LinkType = 'external'

    // Matches !isPadletAttachment.value in WhiteboardPostAttachment
    // TODO: Remove this comment once we have extracted out `play` in WhiteboardPostAttachment
    if (contentSubcategory === 'padlet' && lowercaseProviderName !== 'padlet') {
      linkType = 'padlet'
    } else if (contentCategory === 'photo' || contentCategory === 'file') {
      linkType = 'internal_mediaplayer'
    } else if (contentCategory === 'page' || contentCategory === 'document') {
      linkType = 'internal_webview'
    }

    const displayUrl = urlForDisplay(post, beethovenData)
    if (displayUrl) {
      nativeAppStore.openLink({
        url: displayUrl,
        linkType,
        contentCategory: contentCategory ?? undefined,
        contentSubcategory: contentSubcategory ?? undefined,
      })
    }
  }

  const urlForOpening = (shape: TLShape | null | undefined): string | null => {
    if (!shape) return null

    const postId = (shape as PadletAttachmentShape)?.props?.postId
    if (!postId) return null

    const post = surfacePostsStore.postEntitiesById[postId]
    if (!post?.cid) return null

    const beethovenData = surfaceAttachmentsStore.getLinkDisplayAttributesForPost(post.cid)
    if (!beethovenData) return null

    const interaction = getWhiteboardAttachmentInteraction(beethovenData)

    const isPadletAttachment =
      beethovenData.content_subcategory === 'padlet' && beethovenData.provider_name !== 'padlet'

    // TODO: Uncomment this when we have a way to check if the audio player is playing
    // If we are playing audio in the new audio player, we don't want to open the attachment in a new tab.
    // if (xAudioPlayerV2.value) return null

    // If attachment should be opened in expanded view, we set post permalink
    // as the URL so that cmd/ctrl clicking opens it in a new tab.
    if (interaction === 'expand' && post.permalink) {
      return post.permalink
    }
    const displayUrl = urlForDisplay(post, beethovenData)

    if (!displayUrl) return null

    const url = getUrlForOpening(beethovenData, displayUrl, post.wish_content?.attachment_props?.hotlink)
    if (isPadletAttachment && isViewingTranslatedPadlet.value) {
      return surfaceTranslationsStore.decorateUrlWithTranslationParam(url)
    }
    return url
  }

  return {
    getWhiteboardAttachmentInteraction,
    shouldInteractWithShapeInPlayMode,
    shouldShowInteractOutline,
    executePlayModeActionForShape,
    isMediaContent,
  }
}
